import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause, ShoppingCart, Download, QrCode, X, Music } from 'lucide-react';
import { useAudio } from '@/context/AudioContext';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { usePageEditMode } from '@/context/PageEditModeContext';
import { adminFetch } from '@/utils/adminAuth';

const API = process.env.REACT_APP_BACKEND_URL;

/** Contenteditable span that saves back to the track record. */
const TrackDbEditable = ({ enabled, track, field, parse = (v) => v, format = (v) => String(v ?? ''), className, testid }) => {
  if (!enabled) return <span className={className} data-testid={testid}>{format(track[field])}</span>;
  const save = async (e) => {
    const raw = e.currentTarget.textContent.trim();
    let value;
    try { value = parse(raw); } catch { toast.error('Invalid value'); return; }
    if (value === track[field]) return;
    try {
      const payload = { ...track, [field]: value };
      delete payload._id;
      const r = await adminFetch(`/admin/tracks/${track.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      track[field] = value;
      toast.success(`Updated ${field}`);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    }
  };
  return (
    <span
      contentEditable
      suppressContentEditableWarning
      onBlur={save}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      className={`${className || ''} outline-none ring-1 ring-primary/40 focus:ring-primary rounded px-1 cursor-text`}
      data-testid={testid}
    >{format(track[field])}</span>
  );
};

export const TrackCard = ({ track, isPurchased = false }) => {
  const { currentTrack, isPlaying, playTrack } = useAudio();
  const { addToCart } = useCart();
  const [showQR, setShowQR] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('mp3');
  const { enabled: editEnabled } = usePageEditMode();

  const isCurrentTrack = currentTrack?.id === track.id;
  const isCurrentlyPlaying = isCurrentTrack && isPlaying;
  
  // Check if WAV is available
  const hasWav = track.wav_url && track.wav_price;
  
  // Get current price based on format
  const currentPrice = selectedFormat === 'wav' && hasWav ? track.wav_price : track.price;

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    try {
      await addToCart('track', track.id, { format: selectedFormat });
      toast.success(`Added ${selectedFormat.toUpperCase()} to cart!`);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Already in cart');
      } else {
        toast.error('Failed to add to cart');
      }
    }
  };

  const handleShowQR = async (e) => {
    e.stopPropagation();
    if (!qrCode) {
      try {
        const res = await fetch(`${API}/api/quick-buy/qr/track/${track.id}/base64?format=${selectedFormat}`);
        const data = await res.json();
        setQrCode(data.qr_code);
      } catch (err) {
        console.error('Failed to load QR code:', err);
      }
    }
    setShowQR(true);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className="group relative bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 transition-all duration-300 hover:shadow-2xl"
      data-testid={`track-card-${track.id}`}
    >
      {/* Album Art */}
      <div className="relative aspect-square overflow-hidden">
        <img loading="lazy" decoding="async"
          src={track.cover_url}
          alt={track.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Play Button Overlay */}
        <button
          onClick={() => playTrack(track)}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-primary hover:bg-primary/90 text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 shadow-glow"
          data-testid={`track-play-btn-${track.id}`}
        >
          {isCurrentlyPlaying ? (
            <Pause className="w-8 h-8" fill="currentColor" />
          ) : (
            <Play className="w-8 h-8 ml-1" fill="currentColor" />
          )}
        </button>

        {/* Currently Playing Indicator */}
        {isCurrentTrack && (
          <div className="absolute top-3 right-3 px-3 py-1 bg-primary/90 backdrop-blur-sm text-black text-xs font-bold rounded-full flex items-center gap-2" data-testid={`track-playing-indicator-${track.id}`}>
            <span className="w-2 h-2 rounded-full bg-black animate-pulse" />
            Now Playing
          </div>
        )}
        
        {/* WAV Badge */}
        {hasWav && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-purple-600/90 backdrop-blur-sm text-white text-xs font-bold rounded-full">
            WAV Available
          </div>
        )}
      </div>

      {/* Track Info */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-foreground mb-1 truncate">
          <TrackDbEditable enabled={editEnabled} track={track} field="title" testid={`track-title-${track.id}`} />
        </h3>
        <p className="text-sm text-muted-foreground mb-3 truncate">
          <TrackDbEditable enabled={editEnabled} track={track} field="artist" testid={`track-artist-${track.id}`} />
        </p>

        {/* Format Selector - Only show if WAV is available */}
        {hasWav && !isPurchased && (
          <div className="flex gap-2 mb-3">
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedFormat('mp3'); }}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-all ${
                selectedFormat === 'mp3'
                  ? 'bg-primary text-black'
                  : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
              }`}
            >
              MP3 ${track.price.toFixed(2)}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedFormat('wav'); }}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-all ${
                selectedFormat === 'wav'
                  ? 'bg-purple-600 text-white'
                  : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
              }`}
            >
              WAV ${track.wav_price.toFixed(2)}
            </button>
          </div>
        )}

        {/* Duration & Price */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-muted-foreground font-mono" data-testid={`track-duration-${track.id}`}>
            {formatDuration(track.duration)}
          </span>
          {!hasWav && (
            <span className="text-xl font-bold text-primary font-display">
              $<TrackDbEditable
                enabled={editEnabled}
                track={track}
                field="price"
                parse={(v) => parseFloat(v.replace(/[^\d.]/g, ''))}
                format={(v) => (typeof v === 'number' ? v.toFixed(2) : '0.00')}
                testid={`track-price-${track.id}`}
              />
            </span>
          )}
          {hasWav && (
            <span className={`text-xl font-bold font-display ${selectedFormat === 'wav' ? 'text-purple-400' : 'text-primary'}`}>
              ${currentPrice.toFixed(2)}
              <span className="text-xs ml-1 uppercase">{selectedFormat}</span>
            </span>
          )}
        </div>

        {/* Action Buttons */}
        {isPurchased ? (
          <a
            href={track.download_url}
            download
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full font-bold transition-all hover:scale-105"
            data-testid={`track-download-btn-${track.id}`}
          >
            <Download className="w-4 h-4" />
            Download
          </a>
        ) : (
          <div className="space-y-2">
            <button
              onClick={handleAddToCart}
              className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold transition-all hover:scale-105 shadow-glow ${
                selectedFormat === 'wav' && hasWav
                  ? 'bg-purple-600 hover:bg-purple-500 text-white'
                  : 'bg-primary hover:bg-primary/90 text-primary-foreground'
              }`}
              data-testid={`track-add-to-cart-btn-${track.id}`}
            >
              <ShoppingCart className="w-4 h-4" />
              Add {hasWav ? selectedFormat.toUpperCase() : ''} to Cart
            </button>
            
            {/* QR Code Button */}
            <button
              onClick={handleShowQR}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg"
              data-testid={`track-qr-btn-${track.id}`}
            >
              <QrCode className="w-6 h-6" />
              SCAN TO BUY INSTANTLY
            </button>
          </div>
        )}
      </div>

      {/* QR Code Lightbox — rendered in a portal so parent transforms don't distort it */}
      {showQR && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center"
          onClick={() => setShowQR(false)}
        >
          <div className="absolute inset-0 bg-black/85" />
          
          <div 
            className="relative bg-white rounded-2xl p-8 max-w-xs w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowQR(false)}
              className="absolute -top-3 -right-3 w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="flex items-center gap-2 justify-center mb-1">
              <Music className="w-4 h-4 text-primary" />
              <span className={`text-xs font-bold uppercase ${selectedFormat === 'wav' ? 'text-purple-600' : 'text-primary'}`}>
                {selectedFormat} Track
              </span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-1 text-center">{track.title}</h3>
            <p className="text-gray-500 mb-4 text-center text-sm">{track.artist}</p>
            
            <div className="bg-white p-2 rounded-xl border-4 border-gray-200 mb-4">
              {qrCode ? (
                <img loading="lazy" decoding="async" src={qrCode} alt="QR Code" className="w-full aspect-square" />
              ) : (
                <div className="w-full aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            
            <p className={`text-3xl font-bold mb-2 text-center ${selectedFormat === 'wav' ? 'text-purple-600' : 'text-primary'}`}>
              ${currentPrice.toFixed(2)}
              <span className="text-lg ml-1 uppercase">{selectedFormat}</span>
            </p>
            <p className="text-sm text-gray-500 text-center">Scan with your phone to buy & download</p>
            
            {/* Format switch in lightbox */}
            {hasWav && (
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => { setSelectedFormat('mp3'); setQrCode(null); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold ${
                    selectedFormat === 'mp3' ? 'bg-primary text-black' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  MP3
                </button>
                <button
                  onClick={() => { setSelectedFormat('wav'); setQrCode(null); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold ${
                    selectedFormat === 'wav' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  WAV
                </button>
              </div>
            )}
            
            <p className="text-xs text-gray-400 text-center mt-4">Tap outside or X to close</p>
          </div>
        </div>,
        document.body
      )}
    </motion.div>
  );
};
